import { esc } from "../ui/format.js";
import { uiCard, uiGet, uiHeader, uiKpis, uiMoney } from "../ui/kit.js";
import { emAct, emLetters, emLog, emWireLetters } from "./empire.js";

/**
 * R&D Lab (backend/rnd_lab.py): the research tree for your industry (three
 * branches, four tiers), the project in progress, your patents (exclusive or
 * licensed) and the patent fights your biggest rival starts. On top: Moonshots
 * (backend/moonshots.py), the two giant bets of your industry.
 */

const RD_ICON = { core: "🧠", process: "⚙️", product: "✨" };
const RD_STATUS = { done: "Done", active: "Researching", available: "Ready to start", locked: "Locked" };

let busy = null; // is a project running (set per render): then "Start" becomes "+ Queue"

function rdNode(t) {
  const pct = t.status === "active" ? Math.round((100 * t.progress) / t.months) : t.status === "done" ? 100 : 0;
  return `<div class="rd-node rd-${t.status}">
    <div class="rd-node-top"><span class="rd-tier">Tier ${t.tier}</span>${t.patent ? `<span class="rd-patent" title="Files a patent">📜</span>` : ""}</div>
    <b>${esc(t.name)}</b>
    <span class="rd-effect">${esc(t.effect)}</span>
    <div class="rd-progress"><i style="width:${pct}%"></i></div>
    <em>${RD_STATUS[t.status]}${t.status === "active" ? ` · month ${t.progress} of ${t.months}` : t.status === "done" ? ` · M${t.done_month}` : ` · ${uiMoney(t.cost, { compact: true })} over ${t.months} months`}</em>
    ${t.queued ? `<span class="rd-queued">In the queue · #${t.queued}</span>` : ""}
    <div class="rd-node-act">${t.status === "available" && !busy ? `<button type="button" class="primary" data-rd-start="${t.branch}:${t.tier}">Start</button>` : ""}
      ${(t.status === "available" || t.status === "locked") && !t.queued && busy !== null ? `<button type="button" data-rd-queue="${t.branch}:${t.tier}">+ Queue</button>` : ""}</div>
  </div>`;
}

function rdTree(lab) {
  return `<div class="rd-tree">${lab.branches.map((b) => `
    <div class="rd-branch"><div class="rd-branch-head">${RD_ICON[b.key]} <b>${esc(b.theme)}</b><span>${esc(b.label)}</span></div>
      <div class="rd-chain">${b.tiers.map(rdNode).join(`<div class="rd-link" aria-hidden="true"></div>`)}</div></div>`).join("")}</div>`;
}

/** What happens when the running project finishes: the queue, or the lab's own pick. */
function rdNext(lab) {
  const q = lab.queue;
  return `<div class="rd-next">
    <div class="rd-next-main"><span class="sx-eyebrow">When this project finishes</span>
      ${q.length ? `<ol class="rd-q">${q.map((x) => `<li>${esc(x.name)}</li>`).join("")}</ol>`
        : lab.auto && lab.next_up ? `<p>The lab starts <b>${esc(lab.next_up.name)}</b> on its own.</p>`
        : `<p>${lab.next_up ? "The lab waits for you." : "Every project is done."}</p>`}
      <p class="sx-card-sub">Tap <b>+ Queue</b> on any project to line it up (up to ${lab.queue_max}).</p></div>
    <div class="rd-next-side">
      <label class="rd-auto"><input type="checkbox" data-rd-auto ${lab.auto ? "checked" : ""}> Keep researching automatically</label>
      ${q.length ? `<button type="button" class="ofr-quiet" data-rd-qclear>Clear the queue</button>` : ""}</div>
  </div>`;
}

/** Finished projects: working inside the company, and ready to become a product for customers. */
function rdInnovations(lab) {
  if (!lab.innovations.length) return "";
  return uiCard("💡 Your innovations", `<div class="rd-inv">${lab.innovations.map((v) => `<div class="rd-inv-row">
      <div><b>${esc(v.title)}</b><span>✅ In use across the company: ${esc(v.company_effect)}</span>
        <span>${v.status === "launched" ? `🚀 Launched to customers as <b>${esc(v.product_name)}</b>` : `🛒 Ready to launch for customers: ${esc(v.launch_bonus)}`}</span></div>
      ${v.status === "launched" ? "" : `<button type="button" class="primary" data-rd-launch="${v.id}">Launch it as a product</button>`}</div>`).join("")}</div>`,
    { sub: "Every finished project works inside the company at once. Launch it as a product and customers get it too." });
}

function rdPatents(lab) {
  if (!lab.patents.length) return `<p class="sx-card-sub">No patents yet. Every branch's third tier is a breakthrough that files one.</p>`;
  return `<div class="em-cards">${lab.patents.map((p) => `
    <div class="em-tile rd-pat rd-pat-${p.status}"><div class="em-tile-icon">📜</div><b>${esc(p.title)}</b>
      <span>${p.status === "pending" ? `Filed M${p.filed}; granted around M${p.grants_month}.` : p.status === "granted" ? (p.mode === "licensed" ? `Licensed: ${uiMoney(p.royalty_month)} a month in royalties (${uiMoney(p.royalties, { compact: true })} so far).` : "Exclusive: nobody else may use it. A small, lasting edge in growth.") : p.status === "eroded" ? "Copied by a rival: no longer an edge." : "Cancelled by the patent office."}</span>
      ${p.status === "granted" ? `<div class="em-actions">${p.mode === "licensed"
        ? `<button type="button" data-rd-mode="${p.id}:exclusive">Make it exclusive</button>`
        : `<button type="button" data-rd-mode="${p.id}:licensed">License it out</button>`}</div>` : ""}</div>`).join("")}</div>`;
}


/** Moonshots (backend/moonshots.py): your industry's two giant bets, the race, the result. */
function rdMoonshots(ms) {
  if (!ms || !Array.isArray(ms.options)) return "";
  const b = ms.building;
  const pushes = [["lean", "Lean"], ["full", "Full"], ["allin", "All-in"]];
  const body = b ? `<div class="ms-live">
      <div class="ms-icon">${b.icon}</div>
      <div class="ms-live-main"><b>${esc(b.name)}</b><p>${esc(b.pitch)}</p>
        <div class="ms-bar"><i style="width:${b.progress_pct}%"></i></div>
        <div class="ms-live-facts"><span>${b.months_left} month${b.months_left === 1 ? "" : "s"} to go</span><span>${Math.round(b.odds * 100)}% chance it works</span>
          <span>${uiMoney(b.spent, { compact: true })} of ${uiMoney(b.cost, { compact: true })} spent</span></div>
        <p class="ms-race ${/ahead/.test(b.rival_status) ? "bad" : ""}">🏁 ${esc(b.rival)} ${esc(b.rival_status)}.</p>
        <button type="button" class="ofr-quiet" data-ms-abandon>Abandon it</button></div></div>`
    : `<div class="ms-grid">${ms.options.map((o) => `<article class="ms-card">
        <div class="ms-icon">${o.icon}</div><b>${esc(o.name)}</b><p>${esc(o.pitch)}</p>
        <div class="ms-push" data-ms="${esc(o.key)}">${pushes.map(([k, l]) => `<button type="button" data-push="${k}" class="${k === "full" ? "on" : ""}">${l}</button>`).join("")}</div>
        <div class="ms-quote" data-ms-quote="${esc(o.key)}"></div>
        <button type="button" class="primary" data-ms-start="${esc(o.key)}">Start the moonshot</button>
      </article>`).join("")}</div>`;
  const past = ms.past.length ? `<ul class="ms-past">${ms.past.map((x) => `<li class="${esc(x.status)}"><b>${esc(x.name)}</b> ${esc(x.result || x.status)}</li>`).join("")}</ul>` : "";
  return uiCard("🚀 Moonshots", body + past, { sub: ms.how });
}

function rdQuoteHtml(q) {
  return `<div><em>Cost</em><b>${uiMoney(q.cost, { compact: true })}</b></div><div><em>Takes</em><b>${q.months} months</b></div>
    <div><em>Chance</em><b>${Math.round(q.odds * 100)}%</b></div>`;
}

async function renderRnd(main) {
  const [lab, ms] = await Promise.all([uiGet("/rnd"), uiGet("/moonshots").catch(() => null)]);
  const a = lab.active;
  busy = !!a;
  lab.queue = lab.queue || []; lab.innovations = lab.innovations || [];
  main.innerHTML = `
    <div class="sx em">
      ${uiHeader(`R&D Lab — Month ${lab.month}`, "Long bets with permanent rewards. One project at a time; breakthroughs become patents your rivals will want.")}
      ${uiKpis([
        { label: "Researching", value: a ? esc(a.name) : "Nothing", sub: a ? `month ${a.progress} of ${a.months}` : "pick a project below" },
        { label: "Projects done", value: `${lab.completed} of 12` },
        { label: "Growth from research", value: `+${lab.effects.growth_pts} pts`, sub: "every month, for good" },
        { label: "Cost of goods saved", value: `−${lab.effects.cogs_pts} pts`, sub: "of revenue, for good" },
        { label: "Royalties", value: uiMoney(lab.effects.royalties_month, { compact: true }), sub: "a month" },
      ])}
      ${lab.open.length ? uiCard("Waiting for you", emLetters(lab.open)) : ""}
      ${a || lab.queue.length ? uiCard("What's next", rdNext(lab)) : ""}
      ${rdInnovations(lab)}
      ${rdMoonshots(ms)}
      ${uiCard(`The ${esc(lab.industry)} research tree`, rdTree(lab), { sub: lab.rules, actions: a ? `<button type="button" data-rd-cancel>Stop the current project</button>` : "" })}
      <div class="em-grid">
        ${uiCard("Patents", rdPatents(lab))}
        ${uiCard("Patent fights", emLog(lab.history, { emptyText: "No fights yet. Your biggest rival will notice your patents." }))}
      </div>
    </div>`;
  emWireLetters(main);
  main.querySelectorAll("[data-rd-start]").forEach((b) => b.addEventListener("click", () => {
    const [branch, tier] = b.dataset.rdStart.split(":");
    emAct("rnd_start", { branch, tier: +tier });
  }));
  main.querySelectorAll("[data-rd-queue]").forEach((b) => b.addEventListener("click", () => {
    const [branch, tier] = b.dataset.rdQueue.split(":");
    emAct("rnd_queue_add", { branch, tier: +tier });
  }));
  main.querySelector("[data-rd-auto]")?.addEventListener("change", (e) => emAct("rnd_auto", { on: e.target.checked }));
  main.querySelector("[data-rd-qclear]")?.addEventListener("click", () => emAct("rnd_queue_clear"));
  main.querySelectorAll("[data-rd-launch]").forEach((b) => b.addEventListener("click", () => {
    const v = lab.innovations.find((x) => x.id === +b.dataset.rdLaunch);
    window.openLaunchProductModal({ innovation: v });
  }));
  main.querySelectorAll("[data-rd-mode]").forEach((b) => b.addEventListener("click", () => {
    const [id, mode] = b.dataset.rdMode.split(":");
    emAct("rnd_patent_mode", { id: +id, mode });
  }));
  if (ms && Array.isArray(ms.options) && !ms.building) {
    const push = Object.fromEntries(ms.options.map((o) => [o.key, "full"]));
    const paintQuote = (key) => { main.querySelector(`[data-ms-quote="${key}"]`).innerHTML = rdQuoteHtml(ms.options.find((o) => o.key === key).quotes[push[key]]); };
    ms.options.forEach((o) => paintQuote(o.key));
    main.querySelectorAll("[data-ms] [data-push]").forEach((btn) => btn.addEventListener("click", () => {
      const key = btn.closest("[data-ms]").dataset.ms;
      push[key] = btn.dataset.push;
      btn.parentElement.querySelectorAll("button").forEach((x) => x.classList.toggle("on", x === btn));
      paintQuote(key);
    }));
    main.querySelectorAll("[data-ms-start]").forEach((btn) => btn.addEventListener("click", () => {
      const o = ms.options.find((x) => x.key === btn.dataset.msStart), q = o.quotes[push[o.key]];
      if (confirm(`Start ${o.name} (${q.push_label})? ${uiMoney(q.cost)} over ${q.months} months, ${Math.round(q.odds * 100)}% chance it works.`)) emAct("moonshot_start", { key: o.key, push: push[o.key] });
    }));
  }
  main.querySelector("[data-ms-abandon]")?.addEventListener("click", () => { if (confirm("Abandon the moonshot? Everything spent so far is lost.")) emAct("moonshot_abandon"); });
  const c = main.querySelector("[data-rd-cancel]");
  if (c) c.addEventListener("click", () => { if (confirm("Stop this project? The money already spent is gone.")) emAct("rnd_cancel"); });
}

window.renderRnd = renderRnd;

export { renderRnd };
